import { useCallback, useState } from "react";
import { logConnectorFailure } from "../../../telemetry";

const ACK_STORAGE_KEY = "aiva_calendar_acks_v1";
/** Oldest "Leave It" choices are dropped beyond this many. */
const MAX_ACKS = 50;

function readAcks(): Record<string, string> {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(ACK_STORAGE_KEY) ?? "{}");
    return parsed && typeof parsed === "object" ? (parsed as Record<string, string>) : {};
  } catch (e: unknown) {
    // Unreadable or blocked storage: start with no remembered choices.
    logConnectorFailure("calendar", e, "read-conflict-acks");
    return {};
  }
}

/**
 * Clashes the user chose to "Leave It", remembered on this device. Each entry
 * stores the pair's time signature, so moving either meeting brings the clash back.
 */
function writeAcks(next: Record<string, string>): void {
  try {
    localStorage.setItem(ACK_STORAGE_KEY, JSON.stringify(next));
  } catch (e: unknown) {
    // Storage blocked (e.g. private window): the choice lasts for this session only.
    logConnectorFailure("calendar", e, "write-conflict-acks");
  }
}

export function useConflictAcks(): {
  acks: Readonly<Record<string, string>>;
  acknowledge: (key: string, signature: string) => void;
  /** Undo a "Leave It", so the clash shows again. */
  forget: (key: string) => void;
} {
  const [acks, setAcks] = useState<Record<string, string>>(readAcks);

  const acknowledge = useCallback((key: string, signature: string) => {
    setAcks((prev) => {
      const next = Object.fromEntries(Object.entries({ ...prev, [key]: signature }).slice(-MAX_ACKS));
      writeAcks(next);
      return next;
    });
  }, []);

  const forget = useCallback((key: string) => {
    setAcks((prev) => {
      const next = Object.fromEntries(Object.entries(prev).filter(([k]) => k !== key));
      writeAcks(next);
      return next;
    });
  }, []);

  return { acks, acknowledge, forget };
}
